import React, { useState } from "react";
import { Row, Col } from "react-bootstrap";
import Modal from "~/shared/ModalWithTopBar";
import { FeedbackItemCreateProps } from "~/services/feedbackItem";
import FeedbackJob, { FeedbackJobCreateProps } from "~/services/feedbackJob";
import Title from "~/shared/Title";
import Rating from "~/shared/Rating";
import TextareaInput from "~/shared/TextareaInput";
import Button from "~/shared/Btn";
import styled from "styled-components";
import { useToast } from "~/hooks/useToast";
import useAuth from "~/hooks/useAuth";

const Content = styled.div`
    span {
        font-size: clamp(1rem, 1.1rem + 1vw, 1.8rem);
        font-weight: 600;
    }

    p {
        font-size: 1rem;
        line-height: 2rem;
    }

    .bar {
        border-top: 0.5px solid #ccc;
        margin: 1rem 0;
    }
`;

type EvaluationModalProps = {
    isOpen: boolean;
    onClose: () => void;
    feedbackItens: FeedbackItemCreateProps[];
    jobDetails: Record<string, any>;
    feedbackJobList?: FeedbackJobCreateProps[];
    afterRating: () => void;
    numberOfVideos: number;
};

const EvaluationModal = ({
    isOpen,
    onClose,
    feedbackItens,
    jobDetails,
    feedbackJobList,
    afterRating,
    numberOfVideos,
}: EvaluationModalProps) => {
    const { addError, addSuccess } = useToast();
    const { user } = useAuth();
    const [values, setValues] = useState<Record<string, any>>();
    const [loading, setLoading] = useState<string[]>([]);
    const [customerFeedbackComment, setCustomerFeedbackComment] = useState<string>();

    const onRate = async (data: FeedbackJobCreateProps) => {
        return FeedbackJob.create(data)
            .then(() => afterRating())
            .catch((e) => {
                addError({
                    title: "Não foi possível avaliar o job",
                    description: e?.response?.data?.msg || e.message,
                });
            });
    };

    const saveRating = (name: string, value: string, feedbackItemId?: number) => {
        setLoading((r) => r.concat([name]));
        setValues((prev) => ({ ...prev, [name]: value }));

        const onRateData: FeedbackJobCreateProps = {
            jobId: jobDetails?.id,
            customerId: user?.id_clientes,
            evaluator: "cliente",
            grade: parseInt(value),
            feedbackItemEvaluationId: feedbackItemId,
            customerFeedbackComment: ["customerFeedbackComment"].includes(name) ? value : "",
        };

        onRate(onRateData)
            .then(() => {
                if (["customerFeedbackComment"].includes(name)) {
                    addSuccess({ title: "Avaliação enviada com sucesso." });
                    onClose();
                }
            })
            .catch((e) => {
                addError({ title: "Não foi possível avaliar o job", description: e?.response?.data?.msg ?? e.message });
            })
            .finally(() => setLoading((r) => r.filter((i) => i !== name)));
    };

    // console.log({ jobDetails, user });

    return (
        <Modal isOpen={isOpen} onHide={onClose}>
            <Content>
                <Title level="2">Avaliação do Job / Fotógrafo</Title>
                <p>
                    Pensando em melhorar cada vez mais nosso serviços, gostaríamos de pedir que preencha essa pequena
                    avaliação sobre o fotógrafo e o job executado.
                </p>
                {feedbackItens && feedbackItens.length ? (
                    <>
                        {feedbackItens
                            .filter((item) => {
                                if (numberOfVideos <= 0 && item.name === "Qualidade do vídeo") return;
                                return item;
                            })
                            .map((item) => (
                                <Col key={item.id} className="mt-2">
                                    <div className="bar"></div>
                                    <Rating
                                        name={item.name}
                                        onChange={(name, value) => saveRating(name, value, item.id)}
                                        loading={loading.includes(item.name)}
                                        value={
                                            feedbackJobList.find((fj) => fj.feedbackName === item.name)
                                                ? feedbackJobList.find((fj) => fj.feedbackName === item.name)?.grade
                                                : values?.[item.name]
                                        }
                                        spaceContent
                                    />
                                </Col>
                            ))}
                        <div className="bar"></div>
                    </>
                ) : (
                    ""
                )}
                <div className="mt-4">
                    <TextareaInput
                        name="customerFeedbackComment"
                        onChange={(_, value) => setCustomerFeedbackComment(value)}
                        value={customerFeedbackComment || jobDetails?.execution?.customerFeedbackComment}
                        rows={7}
                        placeholder="Comentário sobre o trabalho"
                    />
                    <small>
                        Comentário que será visualizado pelo fotógrafo. Pode ser um elogio ou uma explicação que ajude
                        ele a entender a avaliação e melhorar o trabalho.
                    </small>
                </div>

                <Row className="mt-4">
                    <Col xs={4}>
                        <Button variant="secondary" block onClick={onClose}>
                            Sair
                        </Button>
                    </Col>
                    <Col xs={8}>
                        <Button
                            variant="success"
                            block
                            loading={loading.includes("customerFeedbackComment")}
                            onClick={() => saveRating("customerFeedbackComment", customerFeedbackComment)}
                        >
                            Enviar
                        </Button>
                    </Col>
                </Row>
            </Content>
        </Modal>
    );
};

export default EvaluationModal;
